import React, { useEffect, useState } from 'react';
import styles from './LeftMenu.module.scss';
import { Flex } from 'antd';

import { ReactComponent as PencilIcon } from '../../assets/images/global-icons/pencil_ic.svg';
import { ReactComponent as PhoneIcon } from '../../assets/images/global-icons/phone_ic.svg';
import { ReactComponent as MailIcon } from '../../assets/images/global-icons/email_ic.svg';
import { ReactComponent as KeyIcon } from '../../assets/images/global-icons/key_ic.svg';
import { ReactComponent as WorkIcon } from '../../assets/images/global-icons/work_ic.svg';
import { ReactComponent as LogoutIcon } from '../../assets/images/global-icons/logout_ic.svg';
import { ReactComponent as BinIcon } from '../../assets/images/global-icons/bin_ic.svg';
import { ReactComponent as HomeFilledIcon } from '../../assets/images/global-icons/home_ic.svg';
import { ReactComponent as HistoryIcon } from '../../assets/images/global-icons/list_ic.svg';
import { ReactComponent as CrmIcon } from '../../assets/images/global-icons/crm_ic.svg';
import { ReactComponent as MembersIcon } from '../../assets/images/global-icons/members_ic.svg';
import { ReactComponent as LinkIcon } from '../../assets/images/global-icons/links_ic.svg';
import { ReactComponent as PersoneIcon } from '../../assets/images/global-icons/persone_ic.svg';
import { ReactComponent as MapIcon } from '../../assets/images/global-icons/map_ic.svg';

import NavButton from './components/NavButton/NavButton';
import TabButton from './components/TabButton/TabButton';
import { useDeleteProfileMutation, useGetMeQuery, useLogoutMutation } from '../../redux/API/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useNavigate } from 'react-router';
import { logout } from '../../redux/isAuthSlice';
import EditEmailModal from './components/EditEmailModal/EditEmailModal';
import EditPhoneNumberModal from './components/EditPhoneNumberModal/EditPhoneNumberModal';
import EditProfileModal from './components/EditProfileModal/EditProfileModal';
import ChangePasswordModal from '../UI/ChangePasswordModal/ChangePasswordModal';
import EditCompanyLinkModal from './components/EditCompanyLinkModal/EditCompanyLinkModal';
import { useLocation } from 'react-router-dom';
import ForgotPasswordModal from '../UI/ChangePasswordModal/ForgotPasswordModal/ForgotPasswordModal';
import ConfirmationPopup from '../UI/ConfirmationPopup/ConfirmationPopup';
import ChangeCompanyModal from './components/ChangeCompanyModal/ChangeCompanyModal';
import Toast from "./components/Toast/Toast";

const BASE_IMAGE_URL = process.env.REACT_APP_FILES_BASE_URL || '';

interface UserDataInterface {
  accountType: string;
  authType: string;
  avatarKey: string;
  company: any;
  description: string;
  createdAt: string;
  email: string;
  firstName: string;
  id: string;
  isBlocked: boolean;
  isDeleted: boolean;
  lastName: string;
  phone: string;
  updatedAt: string;
  leftReviewsCount: number;
  status: string;
}

const LeftMenu = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [userData, setUserData] = useState<UserDataInterface | undefined>();
  const { isAuth } = useSelector((state: RootState) => state.isAuth);

  const [isEditEmailModalOpen, setIsEditEmailModalOpen] = useState(false);
  const [isEditPhoneModalOpen, setIsEditPhoneModalOpen] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isEditCompanyLinkModalOpen, setIsEditCompanyLinkModalOpen] = useState(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [isMembersPageActive, setIsMembersPageActive] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isLogoutModalOpen, setIsLogoutModelOpen] = useState(false);
  const [isChangeCompanyModalOpen, setIsChangeCompanyModalOpen] = useState(false);
  const [isChangePhoneToastOpen, setIsChangePhoneToastOpen] = useState(false);

  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'home' | 'history' | 'map' |''>(() => {
    if (location.pathname.startsWith('/history')) return 'history';
    if (location.pathname.startsWith('/map')) return 'map';
    return 'home';
  });

  const [logoutUser] = useLogoutMutation();
  const [deleteAccount] = useDeleteProfileMutation();

  const { data: user, refetch } = useGetMeQuery({ accessToken: isAuth });

  useEffect(() => {
    if (location.pathname === '/company-members') {
      setIsMembersPageActive(true);
    } else {
      setIsMembersPageActive(false);
    }
  }, [location]);

  useEffect(() => {
    setUserData(user?.data);
  }, [user]);

  useEffect(() => {
    if (location.pathname.startsWith('/history')) {
      setActiveTab('history');
    } else if (location.pathname === '/') {
      setActiveTab('home');
    } else if (location.pathname === '/map') {
      setActiveTab('map');
    } else {
      setActiveTab('');
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await logoutUser({
      body: {
        userId: userData?.id || ''
      },
      accessToken: isAuth
    });

    dispatch(logout());

    navigate('/', { replace: true });
  };

  const handleAccountDelete = async () => {
    try {
      await deleteAccount({
        accessToken: isAuth,
        id: userData?.id ?? ''
      }).unwrap();

      dispatch(logout());

      navigate('/', { replace: true });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Flex vertical className={styles.LeftMenu}>
      <ConfirmationPopup
        isModalOpen={isDeleteAccountModalOpen}
        handleCancel={() => setIsDeleteAccountModalOpen(false)}
        handleClick={handleAccountDelete}
        text={'Are you sure you want to delete your account?'}
      />
      <ConfirmationPopup
        isModalOpen={isLogoutModalOpen}
        handleCancel={() => setIsLogoutModelOpen(false)}
        handleClick={() => handleLogout()}
        text={'Are you sure you want to logout?'}
        removeText={'Logout'}
      />
      <ChangeCompanyModal
        visible={isChangeCompanyModalOpen}
        onClose={() => setIsChangeCompanyModalOpen(false)}
        userId={userData?.id ?? ''}
        refetch={refetch}
        userStatus={userData?.status}
        userCompanyId={userData?.company?.id}
      />
      {userData?.authType === 'email' && (
        <EditEmailModal
          userId={userData?.id || ''}
          email={userData?.email || ''}
          visible={isEditEmailModalOpen}
          onClose={(wasConfirmed) => {
            setIsEditEmailModalOpen(false);
          }}
        />
      )}

      <EditPhoneNumberModal
        visible={isEditPhoneModalOpen}
        onClose={() => setIsEditPhoneModalOpen(false)}
        userId={userData?.id}
        phone={userData?.phone}
        setIsChangePhoneToastOpen={setIsChangePhoneToastOpen}
      />
      {
        isChangePhoneToastOpen &&
        <Toast onClose={() => setIsChangePhoneToastOpen(false)} content="Your number has been successfully verified" />
      }
      <EditProfileModal
        visible={isEditProfileModalOpen}
        onClose={() => setIsEditProfileModalOpen(false)}
        user={userData}
        companyRefetch={refetch}
      />
      <ChangePasswordModal
        visible={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        onForgotPasswordClick={() => {
          setIsChangePasswordModalOpen(false);
          setForgotPasswordEmail(userData?.email || '');
          setIsForgotPasswordModalOpen(true);
        }}
      />

      <ForgotPasswordModal
        open={isForgotPasswordModalOpen}
        onClose={() => setIsForgotPasswordModalOpen(false)}
        email={forgotPasswordEmail}
        onComplete={() => {
          setIsForgotPasswordModalOpen(false);
        }}
      />
      <EditCompanyLinkModal
        visible={isEditCompanyLinkModalOpen}
        onClose={() => setIsEditCompanyLinkModalOpen(false)}
        link={userData?.company?.link}
        id={userData?.id}
        company={userData?.company}
        refetch={refetch}
      />
      <Flex vertical gap={'10px'} className={styles.LeftMenu__title}>
        <h3>Welcome to Krankque! 👋</h3>
        <p>
          Share your <b>feedback</b> and help get to know customers better!
        </p>
      </Flex>

      <Flex vertical gap={'15px'} className={styles.LeftMenu__header}>
        <Flex justify={'space-between'} align={'center'}>
          <Flex justify={'space-between'} align={'center'} gap={'8px'}>
            <>
              {userData?.accountType === 'personal' ? (
                <>
                  {userData && userData.avatarKey ? (
                    <img
                      className={styles.LeftMenu__header__logo}
                      src={`${BASE_IMAGE_URL}/${userData.avatarKey}`}
                      alt="logo"
                    />
                  ) : (
                    <PersoneIcon className={styles.LeftMenu__header__logo} color={'#919191'} />
                  )}
                </>
              ) : (
                <>
                  {userData && userData?.company?.picture ? (
                    <img
                      className={styles.LeftMenu__header__logo}
                      src={`${BASE_IMAGE_URL}/${userData.company?.picture}`}
                      alt="logo"
                    />
                  ) : (
                    <PersoneIcon className={styles.LeftMenu__header__logo} color={'#919191'} />
                  )}
                </>
              )}
            </>

            <Flex vertical className={styles.LeftMenu__header__content}>
              {userData?.accountType === 'personal' ? (
                <span>
                  {userData?.firstName || 'Name'} {userData?.lastName}
                </span>
              ) : (
                <span>{userData?.company?.companyName || 'Company Name'}</span>
              )}

              <p>Left reviews: {userData?.leftReviewsCount}</p>
            </Flex>
          </Flex>
          <PencilIcon
            className={styles.LeftMenu__header__edit}
            color={'#000000'}
            onClick={() => setIsEditProfileModalOpen(true)}
          />
        </Flex>
        <p>{userData?.accountType === 'personal' ? userData?.description : userData?.company?.description}</p>
      </Flex>

      <Flex vertical>
        <NavButton
          Icon={PhoneIcon}
          title={'Phone number'}
          description={`+${userData?.phone}`}
          onClick={() => setIsEditPhoneModalOpen(true)}
        />
        {
          userData?.accountType !== 'personal' && userData?.authType === 'email' &&
          <NavButton
            Icon={MailIcon}
            title={'E-mail'}
            description={userData?.email}
            onClick={() => setIsEditEmailModalOpen(true)}
          />
        }
        {userData?.accountType === 'personal' ? (
          <>
            <NavButton
              Icon={WorkIcon}
              title={'Work at'}
              description={userData?.company?.companyName}
              status={userData?.status ?? ''}
              onClick={() => setIsChangeCompanyModalOpen(true)}
            />
            {
              userData?.authType === 'email' &&
              <NavButton
                Icon={MailIcon}
                title={'E-mail'}
                description={userData?.email}
                onClick={() => setIsEditEmailModalOpen(true)}
              />
            }
          </>
        ) : (
          <>
            <NavButton Icon={CrmIcon} onClick={() => navigate('/crm')} title={'CRM integration'} />
            <NavButton
              Icon={MembersIcon}
              title={'Company members'}
              description={`${userData?.company?.membersCount} members`}
              onClick={() => navigate('/company-members')}
              isActive={isMembersPageActive}
            />
            <NavButton
              Icon={LinkIcon}
              title={'Link'}
              description={userData?.company?.link}
              onClick={() => setIsEditCompanyLinkModalOpen(true)}
            />
          </>
        )}
        {userData?.authType === 'email' && (
          <NavButton Icon={KeyIcon} title={'Change password'} onClick={() => setIsChangePasswordModalOpen(true)} />
        )}
        <NavButton Icon={LogoutIcon} title={'Logout'} onClick={() => setIsLogoutModelOpen(true)} />
        <NavButton Icon={BinIcon} title={'Delete Account'} onClick={() => setIsDeleteAccountModalOpen(true)} />
      </Flex>

      <Flex vertical gap={'8px'} className={styles.LeftMenu__tabs}>
        <TabButton
          Icon={HomeFilledIcon}
          title={'Home'}
          isActive={activeTab === 'home'}
          onClick={() => {
            setActiveTab('home');
            navigate('/');
          }}
        />
        <TabButton
          Icon={MapIcon}
          title={'Map'}
          isActive={activeTab === 'map'}
          onClick={() => {
            setActiveTab('map');
            navigate('/map');
          }}
        />
        <TabButton
          Icon={HistoryIcon}
          title={'History'}
          isActive={activeTab === 'history'}
          onClick={() => {
            setActiveTab('history');
            navigate('/history');
          }}
        />
      </Flex>
    </Flex>
  );
};

export default LeftMenu;
